import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Button, Typography } from '@la/ds-ui-components';
import { getSiteId, useAppSelector } from '@la/services';
import { Activity } from '@la/types';
import { getLAHostnameParts } from '@la/utilities';
import {
  useGetCheckInListQuery,
  usePostCheckInMutation,
  useUpdateCheckInMutation,
} from 'redux/services/singleActivityApi';
import {
  CheckIn,
  CheckInStatus,
  UserSummary,
} from 'redux/services/types/singleActivity';
import { getInitials } from 'domains/SingleActivityView/utils/singleActivityView.utils';
import styles from './ActivityCheckIn.module.scss';

export default function ActivityCheckIn({
  activity,
  userSummaries,
}: Readonly<{
  activity?: Activity;
  userSummaries: UserSummary[];
}>) {
  const { subdomain } = getLAHostnameParts();
  const { programId = '0' } = useParams();
  const siteId = useAppSelector(getSiteId) ?? '';

  const activityTeamIds = activity?.teams?.map((team) => team.teamId) || [];
  const idsOfDisplayableTeams = userSummaries[0].teamIds.filter((teamId) =>
    activityTeamIds.includes(teamId)
  );

  const [postCheckIn] = usePostCheckInMutation();
  const [updateCheckIn] = useUpdateCheckInMutation();

  const { data: checkinData, isLoading } = useGetCheckInListQuery({
    activityId: activity?.id ?? 0,
    siteId,
    programId: parseInt(programId),
    subdomain,
    teamIds: idsOfDisplayableTeams.join(',') ?? '',
  });

  const handlePostCheckIn = (status: CheckInStatus, userId: number) => {
    const requestBody = {
      activityId: activity?.id ?? 0,
      userId,
      subdomain,
      status,
    };

    postCheckIn(requestBody)
      .unwrap()
      .then(() => {
        console.log('CheckIn posted successfully');
      })
      .catch(() => {
        console.error('Error posting checkin');
      });
  };

  const handleUpdateCheckIn = (status: CheckInStatus, userId: number) => {
    const requestBody = {
      activityId: activity?.id ?? 0,
      userId,
      subdomain,
      status,
    };

    updateCheckIn(requestBody)
      .unwrap()
      .then(() => {
        console.log('CheckIn updated successfully');
      })
      .catch(() => {
        console.error('Error updating checkin');
      });
  };

  if (isLoading) {
    return (
      <Typography variant="ui" size="large" weight="bold">
        Loading...
      </Typography>
    );
  }

  return (
    <>
      <ul className={styles.userList}>
        {checkinData?.map((record: CheckIn) => (
          <CheckInUser
            postHanlder={handlePostCheckIn}
            updateHandler={handleUpdateCheckIn}
            record={record}
            key={record.userId}
          />
        ))}
      </ul>
    </>
  );
}

type CheckInUserProps = {
  record: CheckIn;
  postHanlder: (status: CheckInStatus, userId: number) => void;
  updateHandler: (status: CheckInStatus, userId: number) => void;
};

function CheckInUser({ record, postHanlder, updateHandler }: CheckInUserProps) {
  const { userId, firstName, lastName, checkinStatus, lastCheckedDate } =
    record;
  return (
    <li key={userId} className={styles.user}>
      <div className={styles.avatar}>{getInitials(firstName, lastName)}</div>
      <Typography variant="ui" size="large" weight="bold">
        {firstName} {lastName}
      </Typography>
      <div className={styles.checkinActions}>
        {!lastCheckedDate && (
          <Button
            variant="primary"
            onClick={() => postHanlder('checkedIn', userId)}
            className={styles.checkinButton}
          >
            Check in
          </Button>
        )}
        {lastCheckedDate && checkinStatus === 'checkedIn' && (
          <>
            <Typography variant="ui" size="medium" weight="bold">
              Checked in
            </Typography>
            <div className={styles.checkedDate}>
              <Typography variant="ui" size="small">
                {DateTime.fromISO(lastCheckedDate).toFormat('MMMM d, h:mm a')}
              </Typography>
              <Button
                variant="text"
                onClick={() => updateHandler('notCheckedIn', userId)}
                size="small"
              >
                Reset
              </Button>
            </div>
          </>
        )}
        {lastCheckedDate && checkinStatus === 'notCheckedIn' && (
          <Button
            variant="primary"
            onClick={() => updateHandler('checkedIn', userId)}
            className={styles.checkinButton}
          >
            Check in
          </Button>
        )}
      </div>
    </li>
  );
}
